import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Auth, User, getAuth, onIdTokenChanged } from 'firebase/auth';

@Injectable({
  providedIn: 'root',
})
export class Authentication {

  private router = inject(Router);

  private auth: Auth;

  private userSig = signal<User | null>(null);
  user = this.userSig.asReadonly();

  private idTokenSig = signal<string | null>(null);
  idToken = this.idTokenSig.asReadonly();

  private readySig = signal(false);
  ready = this.readySig.asReadonly();

  private readonly readyPromise: Promise<void>;

  constructor() {
    this.auth = getAuth();

    this.readyPromise = this.auth.authStateReady().then(() => {
      this.readySig.set(true);
    });

    onIdTokenChanged(this.auth, async (user) => {
      this.userSig.set(user);

      if (!user) {
        this.idTokenSig.set(null);
        return;
      }

      try {
        const token = await user.getIdToken();
        this.idTokenSig.set(token);
      } catch {
        this.idTokenSig.set(null);
      }
    });
  }

  whenReady(): Promise<void> {
    return this.readyPromise;
  }

  async isAuthenticatedAsync(): Promise<boolean> {
    await this.whenReady();
    return this.auth.currentUser !== null;
  }

  get currentUser(): User | null {
    return this.userSig();
  }

  async getIdToken(): Promise<string | null> {
    await this.whenReady();

    const user = this.auth.currentUser;
    if (!user) {
      this.idTokenSig.set(null);
      return null;
    }

    try {
      const token = await user.getIdToken();
      this.idTokenSig.set(token);
      return token;
    } catch {
      this.idTokenSig.set(null);
      return null;
    }
  }

  logout(): void {
    void this.auth.signOut();
    void this.router.navigate(['/']);
  }
}
